import {
  Droplets,
  Zap,
  Thermometer,
  Leaf,
  Paintbrush,
  Home,
  Hammer,
  HardHat,
  Wrench,
  Layers,
  Waves,
  LayoutGrid,
  Shield,
  Bug,
  Building2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface IndustryFaq {
  question: string;
  answer: string;
}

export interface IndustryPage {
  slug: string;
  name: string;
  metaTitle: string;
  metaDescription: string;
  heroHeadline: string;
  heroSubheading: string;
  icon: LucideIcon;
  benefits: string[];
  painPoints: string[];
  solutions: string[];
  faqs: IndustryFaq[];
}

export const industryPages: IndustryPage[] = [
  {
    slug: "plumbers",
    name: "Plumbers",
    metaTitle: "Quoting Software for Plumbers — QuoTrak",
    metaDescription:
      "QuoTrak is purpose-built quoting software for plumbers. Build accurate quotes for callouts, repairs, and full installs, track margin on every job, and convert accepted quotes to invoices in one click. Free to start.",
    heroHeadline: "Quoting software built for plumbers",
    heroSubheading:
      "From a $200 callout to a $20,000 bathroom fit-out — QuoTrak helps you quote accurately, protect your margin, and get paid faster on every job.",
    icon: Droplets,
    benefits: [
      "Quote callouts, repairs, and full installs with accurate part costs",
      "Track margin on high-variance jobs like burst pipes or blocked drains",
      "Convert accepted quotes to invoices instantly after the job",
      "Keep customer history so repeat clients get faster service",
    ],
    painPoints: [
      "Material prices change constantly — a quote built from memory can easily leave you underwater on parts",
      "Clients ask for detailed breakdowns but you're quoting from a napkin or a phone call",
      "Chasing invoice payments manually eats into time you should spend on jobs",
    ],
    solutions: [
      "Build itemized quotes with real labour and material costs so your margin holds up before you commit",
      "Send polished, branded PDFs that break down every line item and build client confidence",
      "Convert approved quotes to invoices in one click and collect deposits upfront via Stripe",
    ],
    faqs: [
      {
        question: "Can I save my common plumbing jobs as templates?",
        answer:
          "Yes. You can save any quote as a reusable template — tap-replacements, hot water installs, drain unblocks, and more. Next time you need to quote a similar job, start from the template and adjust quantities or parts in seconds.",
      },
      {
        question: "How does QuoTrak handle call-out fees vs labour rates?",
        answer:
          "You can add a call-out fee as a separate line item on any quote and set your hourly labour rate alongside it. Each line item can have its own unit, quantity, and markup so the final price is accurate every time.",
      },
      {
        question: "Can I quote material costs and labour separately?",
        answer:
          "Absolutely. QuoTrak lets you itemize materials, labour, and any other cost category as individual line items. Your client sees the total; you see the margin breakdown internally.",
      },
      {
        question: "Does QuoTrak support deposit invoicing for larger plumbing jobs?",
        answer:
          "Yes. When a client accepts a quote for a larger job like a bathroom renovation or system replacement, you can issue a deposit invoice for any percentage or fixed amount, then a final invoice when the job is done — all from the same quote.",
      },
      {
        question: "Is QuoTrak suitable for a sole-trader plumber?",
        answer:
          "QuoTrak is designed for sole traders and small crews alike. The free plan gives you access to quoting and invoicing with no credit card needed, so you can get started right away without upfront cost.",
      },
    ],
  },
  {
    slug: "electricians",
    name: "Electricians",
    metaTitle: "Invoicing & Quoting App for Electricians — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for electricians. Price board upgrades, rewires, and smoke alarms from saved templates, see your margin before you send, and invoice clients in one click. Free to start.",
    heroHeadline: "Quoting and invoicing app built for electricians",
    heroSubheading:
      "Stop leaving money on the table with vague pricing. QuoTrak gives electricians fast, itemized quotes with real margin visibility — so you know exactly what you'll make before you send.",
    icon: Zap,
    benefits: [
      "Build templates for common jobs: board upgrades, smoke alarms, rewires",
      "Price labour and materials together without separate spreadsheets",
      "See margin before you commit to a fixed-price quote",
      "Manage residential and commercial clients in one place",
    ],
    painPoints: [
      "Unit-rate pricing is easy to underestimate when you're quoting from memory or rough notes",
      "Customers compare your quote to budget electricians — without a detailed breakdown, you look expensive",
      "Tracking permit costs, material markups, and labour separately across multiple jobs is a constant juggle",
    ],
    solutions: [
      "Save common items — breakers, cable runs, outlets, smoke alarms — to a reusable cost catalogue for fast quoting",
      "Send itemised PDFs that justify your rate and separate you clearly from low-ball competitors",
      "See your profit margin on every quote before you hit send, every single time",
    ],
    faqs: [
      {
        question: "Can I save board upgrade or rewire jobs as quote templates?",
        answer:
          "Yes. Save any quote as a reusable template so your next board upgrade or rewire starts from a solid baseline. Adjust quantities and materials to suit the job — no starting from scratch each time.",
      },
      {
        question: "How do I price labour and materials together without spreadsheets?",
        answer:
          "Each quote in QuoTrak can have unlimited line items. Add labour as an hourly rate, then add materials as separate line items with quantities and unit costs. QuoTrak totals everything and shows your margin in real time.",
      },
      {
        question: "Can QuoTrak handle both residential and commercial electrical clients?",
        answer:
          "Yes. You can manage all your clients — residential and commercial — from a single dashboard. Create separate quote templates for different client types and track acceptance rates to refine your pricing strategy.",
      },
      {
        question: "Can I include my electrical licence number on quotes and invoices?",
        answer:
          "Your business details — including licence numbers and registration info — can be added to your QuoTrak profile and they appear on every PDF quote and invoice you send to clients.",
      },
      {
        question: "Does QuoTrak work for small electrical businesses and sole traders?",
        answer:
          "Yes. QuoTrak is built for sole-trader electricians and small crews. Start on the free plan with no credit card required and upgrade when your volume grows.",
      },
    ],
  },
  {
    slug: "hvac-technicians",
    name: "HVAC Technicians",
    metaTitle: "Quoting Software for HVAC Technicians — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software for HVAC technicians. Quote installs, servicing, and emergency call-outs separately, track parts costs against quoted price, and invoice with one click. Free to start.",
    heroHeadline: "Quoting software built for HVAC technicians",
    heroSubheading:
      "From a $150 service call to a $15,000 system replacement — QuoTrak helps HVAC techs quote accurately, track parts costs, and protect margin on every type of job.",
    icon: Thermometer,
    benefits: [
      "Quote installs, servicing, and emergency call-outs separately",
      "Track parts costs against quoted price to catch margin bleed",
      "Save service templates and reuse them across similar jobs",
      "Invoice directly from accepted quotes with one click",
    ],
    painPoints: [
      "Equipment and refrigerant prices fluctuate — a quote from last week might already be underwater",
      "Service calls and full system installs have completely different cost structures to manage",
      "Scheduling deposits and progress payments on large installation jobs is messy with manual invoicing",
    ],
    solutions: [
      "Build quotes from a live cost catalogue so your pricing reflects current parts and equipment costs",
      "Use saved templates for common job types — service calls, installations, inspections — and quote in minutes",
      "Issue deposit invoices and staged billing for big system installs, all from the same accepted quote",
    ],
    faqs: [
      {
        question: "Can I save separate templates for servicing vs. system installs?",
        answer:
          "Yes. You can create as many templates as you need. Keep a simple servicing template for routine maintenance calls and a detailed install template for full system replacements — each with their own labour rates and parts lists.",
      },
      {
        question: "How do I handle parts cost variability between quotes?",
        answer:
          "QuoTrak lets you update unit costs on any line item before sending a quote, so your pricing reflects the current price of equipment, refrigerant, or parts — not last month's rate. You see your updated margin in real time as you adjust.",
      },
      {
        question: "Can I collect deposits on large HVAC installation jobs?",
        answer:
          "Yes. Once a client accepts a quote, you can issue a deposit invoice for any amount or percentage, then a final invoice on completion — all linked to the same original quote for clean record-keeping.",
      },
      {
        question: "Does QuoTrak support emergency call-out rates?",
        answer:
          "You can add call-out fees, after-hours rates, and travel costs as individual line items on any quote. Build a template that already includes your standard call-out fee so emergency quotes take seconds to prepare.",
      },
      {
        question: "Is QuoTrak suitable for an HVAC sole trader or small team?",
        answer:
          "Yes. QuoTrak is designed for both solo operators and growing HVAC businesses. The free plan covers quoting and invoicing with no credit card required to get started.",
      },
    ],
  },
  {
    slug: "landscapers",
    name: "Landscapers",
    metaTitle: "Quoting Software for Landscapers — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for landscapers. Itemize labour, materials, and equipment hire, build seasonal templates, and set up recurring billing for maintenance contracts. Free to start.",
    heroHeadline: "Quoting software built for landscapers",
    heroSubheading:
      "Turn seasonal volume into reliable, profitable revenue. QuoTrak helps landscapers quote maintenance contracts and one-off projects consistently — so you can grow your crew without losing track of profit.",
    icon: Leaf,
    benefits: [
      "Itemise labour, materials, and equipment hire on every quote",
      "Build seasonal templates for mowing, pruning, and full redesigns",
      "Track profitability across residential and commercial contracts",
      "Get paid faster with professional invoices sent from the app",
    ],
    painPoints: [
      "Quoting maintenance contracts by gut feel means you often leave money on the table or underprice seasonal work",
      "Seasonal cash flow is unpredictable without recurring billing in place for ongoing clients",
      "Tracking which customers and job types are actually profitable is nearly impossible in a spreadsheet",
    ],
    solutions: [
      "Quote maintenance and one-off jobs from reusable templates so your pricing is always consistent and covers real costs",
      "Set up recurring invoices for monthly and weekly service contracts so you get paid reliably without manual follow-up",
      "See which customers and job types drive your best margins from your QuoTrak dashboard",
    ],
    faqs: [
      {
        question: "Can I build separate templates for maintenance vs. one-off landscaping projects?",
        answer:
          "Yes. You can create unlimited templates — a simple recurring maintenance template with your standard rates, and separate templates for garden redesigns, irrigation installs, or retaining walls with full material lists.",
      },
      {
        question: "Does QuoTrak support recurring billing for ongoing maintenance contracts?",
        answer:
          "Yes. You can set up recurring invoices on a weekly, fortnightly, or monthly schedule for ongoing maintenance clients. QuoTrak generates and sends invoices automatically so you don't have to chase them manually.",
      },
      {
        question: "How do I include equipment hire costs in a landscaping quote?",
        answer:
          "Add equipment hire as a line item with the hire cost per day and number of days. You can itemize it separately so the client sees exactly what they're paying for while you track the margin across materials, labour, and equipment.",
      },
      {
        question: "Can I manage both residential homeowners and commercial property clients?",
        answer:
          "Yes. QuoTrak's customer management lets you track all your clients — residential and commercial — in one place. Each customer has their own quote and invoice history so you always know where things stand.",
      },
      {
        question: "Does QuoTrak work for a landscaping sole trader or small crew?",
        answer:
          "QuoTrak is built for solo operators and small businesses. Start free with no credit card required and upgrade to unlock recurring billing and more advanced features as your business grows.",
      },
    ],
  },
  {
    slug: "painters",
    name: "Painters",
    metaTitle: "Quoting & Invoicing Software for Painters — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for painters. Quote by room, surface area, or whole-of-house, track paint and material costs, and protect your margin on multi-day jobs. Free to start.",
    heroHeadline: "Quoting software built for painters",
    heroSubheading:
      "Price by room, surface, or whole-of-house — and always know your cut. QuoTrak helps painting contractors build fast, accurate quotes and win more jobs with polished proposals.",
    icon: Paintbrush,
    benefits: [
      "Quote by room, surface area, or whole-of-house with ease",
      "Include paint, primer, and prep costs without forgetting anything",
      "Protect margins on multi-day jobs with real-time profit tracking",
      "Reuse interior and exterior templates for faster quoting",
    ],
    painPoints: [
      "Paint and material costs vary by job — a fixed-rate quote often means working for free if coverage runs over",
      "Customers get three quotes before deciding; if yours isn't detailed and professional, you lose",
      "Following up on quotes that haven't been accepted is easy to forget when you're in the middle of a job",
    ],
    solutions: [
      "Build line-item quotes covering prep, primer, paint coats, and finishing so your cost is accurate every time",
      "Send polished, branded PDFs that make your business look established before you've started the job",
      "Track open quotes in your pipeline and follow up with one click before the customer books someone else",
    ],
    faqs: [
      {
        question: "Can I build separate templates for interior and exterior painting jobs?",
        answer:
          "Yes. Save your interior and exterior templates separately — each with their own prep, primer, paint, and finish line items. Start from the right template for each job and adjust quantities in seconds.",
      },
      {
        question: "How do I include paint costs accurately when prices change?",
        answer:
          "Each line item in QuoTrak has an editable unit cost, so you can update paint prices to reflect current supplier pricing before sending any quote. Your margin updates in real time so you always know where you stand.",
      },
      {
        question: "Can I quote multi-room or whole-of-house painting jobs with QuoTrak?",
        answer:
          "Yes. Add as many line items as your job needs — by room, by surface, or by coat. Group them however makes sense for your client's proposal so the quote is easy to read and easy to approve.",
      },
      {
        question: "Does QuoTrak support recurring billing for commercial maintenance painting contracts?",
        answer:
          "Yes. For commercial clients on ongoing painting maintenance agreements, you can set up recurring invoices so you get paid automatically without manually generating an invoice each period.",
      },
      {
        question: "How do I follow up on quotes that haven't been accepted?",
        answer:
          "Your QuoTrak pipeline shows all your open quotes and how long each has been outstanding. You can resend a quote to a client with one click — no need to manually track who hasn't responded.",
      },
    ],
  },
  {
    slug: "roofers",
    name: "Roofers",
    metaTitle: "Quoting Software for Roofers — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software for roofers. Quote re-roofs, repairs, and guttering as separate line items, include material wastage, and split invoices into deposit and progress payments. Free to start.",
    heroHeadline: "Quoting software built for roofers",
    heroSubheading:
      "Quote re-roofs, repairs, and guttering accurately — and protect your margin when material costs run close. QuoTrak gives roofers fast, itemized quotes and professional invoicing in one place.",
    icon: Home,
    benefits: [
      "Quote re-roofs, repairs, and guttering as separate line items",
      "Include material wastage and labour uplift without guesswork",
      "Track margin on large jobs where material costs vary",
      "Convert quotes to invoices with deposit and progress payment splits",
    ],
    painPoints: [
      "Material costs for tiles, metal, and membrane change frequently — outdated rates erode your margin fast",
      "Large re-roof jobs involve multiple phases; managing deposits and progress invoices manually is error-prone",
      "Clients expect detailed proposals covering labour, materials, and disposal — a rough lump sum loses the job",
    ],
    solutions: [
      "Build quotes with live material costs so your pricing reflects current tile, metal, or membrane rates",
      "Issue a deposit invoice when the job is signed off and progress invoices at each milestone — all from one quote",
      "Send detailed, itemized PDFs that break down labour, materials, and removal costs so clients have full confidence",
    ],
    faqs: [
      {
        question: "Can I include material wastage in my roofing quotes?",
        answer:
          "Yes. Add wastage as a percentage uplift on any material line item, or include it as a separate line item. QuoTrak recalculates your total and margin in real time so you can see the impact before you send the quote.",
      },
      {
        question: "How do I handle deposit and progress billing for large re-roof jobs?",
        answer:
          "Once a client accepts your quote, you can issue a deposit invoice for any amount or percentage, followed by progress invoices at agreed milestones, and a final invoice on completion — all linked to the original quote.",
      },
      {
        question: "Can I quote repairs and guttering separately from a full re-roof?",
        answer:
          "Yes. QuoTrak's line-item quoting lets you separate re-roofing labour, materials, guttering, flashings, and disposal into clearly labeled sections so the client understands exactly what they're paying for.",
      },
      {
        question: "Does QuoTrak work for both residential and commercial roofing jobs?",
        answer:
          "Yes. Manage residential homeowners and commercial property clients in the same dashboard. Each client has their own quote and invoice history so you can track every job, past and present.",
      },
      {
        question: "Can I update material prices when supplier costs change?",
        answer:
          "Every line item has an editable unit cost. Before sending any quote, update material costs to current supplier pricing and QuoTrak will recalculate your total and margin automatically.",
      },
    ],
  },
  {
    slug: "carpenters",
    name: "Carpenters",
    metaTitle: "Quoting Software for Carpenters — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for carpenters. Build templates for decks, frames, and custom joinery, price timber and labour together, and know your margin before quoting fixed-price jobs. Free to start.",
    heroHeadline: "Quoting software built for carpenters",
    heroSubheading:
      "Build quotes for decks, frames, fit-outs, and custom joinery — and know your margin before you commit to a fixed price. QuoTrak keeps timber costs, labour, and client invoicing all in one place.",
    icon: Hammer,
    benefits: [
      "Build templates for decks, frames, fit-outs, and custom joinery",
      "Price timber, hardware, and labour in a single quote",
      "Know your profit margin before quoting on fixed-price contracts",
      "Manage residential clients, builders, and commercial clients separately",
    ],
    painPoints: [
      "Timber and hardware prices shift constantly — fixed-price quotes built from old costs often lose money",
      "Custom joinery and bespoke work is hard to price consistently without a structured quoting process",
      "Builders and developers expect professional, detailed proposals — rough estimates lose you contracts",
    ],
    solutions: [
      "Update material unit costs before every quote so your pricing reflects current timber and hardware prices",
      "Build detailed templates for your most common jobs — decks, frames, fit-outs — and adapt them to each client",
      "Send polished, itemised PDFs that meet builder and developer expectations and win you more contracts",
    ],
    faqs: [
      {
        question: "Can I build templates for different types of carpentry work?",
        answer:
          "Yes. Create separate templates for decks, pergolas, framing, fit-outs, and custom joinery. Each template saves your standard line items, materials, and labour rates so you quote consistently across similar jobs.",
      },
      {
        question: "How do I handle varying timber prices in my quotes?",
        answer:
          "Each line item in QuoTrak has an editable unit cost. Before sending a quote, update timber and hardware costs to current supplier pricing — your total and margin update instantly so you always quote at current rates.",
      },
      {
        question: "Can I manage quotes for both homeowners and builders in the same place?",
        answer:
          "Yes. QuoTrak lets you manage all your clients — homeowners, builders, and commercial clients — from one dashboard. Each client has their full quote and invoice history in one place.",
      },
      {
        question: "Does QuoTrak support fixed-price contracts for carpentry jobs?",
        answer:
          "QuoTrak shows you your profit margin in real time as you build a quote, so before you commit to a fixed price you can see exactly what you'll make. This protects you from underquoting on complex custom jobs.",
      },
      {
        question: "Can I split invoices into deposit and final payment for larger carpentry jobs?",
        answer:
          "Yes. Once a quote is accepted, issue a deposit invoice for any amount, then a final invoice on completion. For long projects you can also issue progress invoices at milestones — all from the same original quote.",
      },
    ],
  },
  {
    slug: "general-contractors",
    name: "General Contractors",
    metaTitle: "Quoting Software for General Contractors — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software for general contractors. Build multi-trade quotes with grouped line items, track overall margin as sub-costs come in, and manage all clients and invoices from one dashboard. Free to start.",
    heroHeadline: "Quoting software built for general contractors",
    heroSubheading:
      "Manage multi-trade quotes without losing sight of the bottom line. QuoTrak helps general contractors capture every cost, protect their markup, and deliver professional proposals that win jobs.",
    icon: HardHat,
    benefits: [
      "Quote complex multi-trade jobs with grouped line items",
      "Track overall margin as sub-costs come in during the job",
      "Build proposal-style quotes that look polished to clients",
      "Manage all clients, quotes, and invoices from one dashboard",
    ],
    painPoints: [
      "Juggling multiple subcontractors makes it easy to miss costs and blow your markup before the job is even started",
      "Clients push back on vague lump-sum quotes with no detail — and you lose to contractors who show their working",
      "Tracking what's been invoiced versus quoted across a long project is a constant administrative headache",
    ],
    solutions: [
      "Build multi-trade quotes with every subcontractor, material, and labour cost captured in one organized document",
      "Share professional, itemized PDFs that justify your price and build client confidence from the first proposal",
      "Convert accepted quotes into staged invoices and track progress billing in one place throughout the project",
    ],
    faqs: [
      {
        question: "Can I group line items by trade or category in a general contracting quote?",
        answer:
          "Yes. QuoTrak's line-item quoting lets you organize costs by trade, phase, or category — so your proposal is clear and professional. Clients see the breakdown they expect; you see the total margin across all sections.",
      },
      {
        question: "How do I track subcontractor costs against my quoted price?",
        answer:
          "Enter your internal subcontractor costs as line items with your markup applied. QuoTrak shows you the margin on each line and across the whole quote, so you can see exactly where your profit is coming from.",
      },
      {
        question: "Can I issue progress invoices on long construction projects?",
        answer:
          "Yes. Once a quote is accepted, issue a deposit invoice, then progress invoices at each milestone, and a final invoice on completion — all linked to the original quote for clean records.",
      },
      {
        question: "Does QuoTrak work for both residential and commercial general contracting?",
        answer:
          "Yes. Manage homeowners, developers, and commercial clients from the same dashboard. Each client has their full quote and invoice history so nothing gets lost across long projects.",
      },
      {
        question: "Can I hide internal cost details from the client-facing PDF?",
        answer:
          "QuoTrak's client PDFs show the professional, client-facing view of your quote. Your internal cost breakdowns and margin figures stay within the app and are never visible to clients.",
      },
    ],
  },
  {
    slug: "handymen",
    name: "Handymen",
    metaTitle: "Quoting & Invoicing App for Handymen — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for handymen. Quote small jobs quickly, set hourly or fixed rates, reuse templates for recurring maintenance work, and send professional invoices. Free to start.",
    heroHeadline: "Quoting and invoicing app built for handymen",
    heroSubheading:
      "Quote quickly for small jobs without overcomplicating things. QuoTrak gives handymen a fast, professional way to quote, invoice, and get paid — without the admin overhead.",
    icon: Wrench,
    benefits: [
      "Quote quickly for small jobs without overcomplicating things",
      "Set hourly rates and fixed fees with per-job flexibility",
      "Use saved templates to price recurring maintenance work fast",
      "Send professional invoices that make you look established",
    ],
    painPoints: [
      "Small jobs don't justify complex quoting software — but vague verbal quotes lead to payment disputes",
      "Customers want a written quote or invoice before they pay, and a handwritten note doesn't inspire confidence",
      "Recurring maintenance clients need consistent pricing that's quick to generate each time",
    ],
    solutions: [
      "Create simple, clear quotes in under two minutes — even for small single-item jobs",
      "Send professional branded PDFs that make your business look credible and cut payment disputes",
      "Save your recurring job types as templates and quote regular maintenance clients in seconds",
    ],
    faqs: [
      {
        question: "Is QuoTrak too complicated for a handyman doing small jobs?",
        answer:
          "Not at all. QuoTrak is designed to be fast. For a simple job, you can create a quote with one or two line items in under two minutes and send it straight to the client as a PDF.",
      },
      {
        question: "Can I set an hourly rate for handyman labour?",
        answer:
          "Yes. Add a labour line item with your hourly rate and the number of hours. QuoTrak calculates the total and you can add materials or other costs as separate line items.",
      },
      {
        question: "How do I handle recurring maintenance clients?",
        answer:
          "Save your standard maintenance job as a template. Each time you need to quote a repeat client, start from that template, adjust if needed, and send it in seconds. You can also set up recurring invoices for clients on regular schedules.",
      },
      {
        question: "Can I send invoices directly from my phone?",
        answer:
          "Yes. QuoTrak works on any device with a web browser. Create and send quotes and invoices from your phone on the job — no desktop required.",
      },
      {
        question: "What does QuoTrak cost for a handyman?",
        answer:
          "The free plan covers quoting and invoicing with no credit card required. If you want recurring billing, email sending, and advanced features, you can upgrade to Pro at any time.",
      },
    ],
  },
  {
    slug: "concreters",
    name: "Concreters",
    metaTitle: "Quoting Software for Concreters — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for concreters. Quote slabs, driveways, and paths with accurate m² pricing, separate concrete, formwork, and labour costs, and invoice in stages. Free to start.",
    heroHeadline: "Quoting software built for concreters",
    heroSubheading:
      "Quote slabs, driveways, and paths accurately — and protect your margin on large pours where costs run close. QuoTrak gives concreters itemized quoting, staged invoicing, and professional PDFs.",
    icon: Layers,
    benefits: [
      "Quote slabs, driveways, and paths with accurate m² pricing",
      "Include concrete, formwork, and labour as separate line items",
      "Protect your margin on large pours where costs run close",
      "Invoice in stages — deposit, progress, and final — from one quote",
    ],
    painPoints: [
      "Concrete volume calculations are tight — a small error in m² pricing on a large slab can wipe your profit",
      "Formwork, reinforcement, and finishing are easy to underquote when you're estimating from memory",
      "Large concreting jobs require deposit and progress billing, which is messy to manage with manual invoices",
    ],
    solutions: [
      "Build quotes with m² unit pricing so your slab, driveway, and path jobs are priced accurately every time",
      "Itemize concrete supply, formwork, reinforcement, and finishing separately so nothing gets left out",
      "Issue deposit invoices upfront and progress invoices at each pour — all from the same accepted quote",
    ],
    faqs: [
      {
        question: "Can I build quotes based on m² for concrete slabs and driveways?",
        answer:
          "Yes. Set up a line item with a per-m² unit price and enter the area. QuoTrak multiplies it out, shows the total, and lets you add formwork, reinforcement, and labour as separate line items on the same quote.",
      },
      {
        question: "How do I handle concrete supply price variability?",
        answer:
          "Update the unit cost on your concrete supply line item before every quote to reflect current supplier pricing. Your quote total and margin update in real time so you're always pricing at current rates.",
      },
      {
        question: "Can I manage deposit and progress billing on large concrete jobs?",
        answer:
          "Yes. Issue a deposit invoice when the job is confirmed, progress invoices at key stages like formwork and pour, and a final invoice on completion — all generated from the original accepted quote.",
      },
      {
        question: "Can I include disposal and site prep costs in my concreting quotes?",
        answer:
          "Yes. Add as many line items as the job needs — site prep, excavation, disposal, formwork, concrete, reinforcement, finishing, and sealing. Each appears as a separate line on the client PDF.",
      },
      {
        question: "Does QuoTrak suit a solo concreter or a small concreting business?",
        answer:
          "Yes. QuoTrak works for sole traders and small crews alike. Start free with no credit card required and upgrade when you need features like recurring invoices or advanced analytics.",
      },
    ],
  },
  {
    slug: "pool-spa-technicians",
    name: "Pool & Spa Technicians",
    metaTitle: "Quoting Software for Pool & Spa Technicians — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software for pool and spa technicians. Quote new builds, renovations, and regular servicing separately, itemize equipment, chemicals, and labour, and track profitability. Free to start.",
    heroHeadline: "Quoting software built for pool & spa technicians",
    heroSubheading:
      "From a routine service call to a full pool renovation — QuoTrak helps pool and spa technicians quote accurately, track every cost, and get paid on time for every job.",
    icon: Waves,
    benefits: [
      "Quote new builds, renovations, and regular servicing separately",
      "Itemise equipment, chemicals, labour, and travel costs",
      "Track profitability across seasonal fluctuations in workload",
      "Keep full job and payment history for every client's pool",
    ],
    painPoints: [
      "Seasonal demand swings make it hard to maintain consistent pricing and cash flow throughout the year",
      "Chemical costs and equipment prices change regularly — a quote built on old rates can leave you short",
      "Clients expect detailed service records and quotes — a rough estimate loses trust in a recurring-service business",
    ],
    solutions: [
      "Build templates for service calls, chemical treatments, and renovation quotes so your pricing is always consistent",
      "Update chemical and equipment costs before each quote so your margin is protected at current supplier pricing",
      "Keep a complete service and payment history for every client so nothing gets missed on recurring jobs",
    ],
    faqs: [
      {
        question: "Can I manage recurring service contracts for pool maintenance clients?",
        answer:
          "Yes. Set up recurring invoices for regular maintenance clients on a weekly, fortnightly, or monthly schedule. QuoTrak sends invoices automatically so you don't have to generate them manually each period.",
      },
      {
        question: "How do I itemize chemicals, equipment, and labour on a pool service quote?",
        answer:
          "Add each cost as a separate line item — chemicals by unit, equipment by item, and labour by the hour or fixed fee. QuoTrak totals everything and shows your margin so you know what you're making on each service call.",
      },
      {
        question: "Can I build separate templates for servicing vs. pool renovation quotes?",
        answer:
          "Yes. Keep a simple servicing template for routine chemical treatments and a detailed template for renovation quotes covering equipment, plumbing, tiling, and labour — each with their own line items and rates.",
      },
      {
        question: "Does QuoTrak help manage pool service jobs across seasonal demand changes?",
        answer:
          "QuoTrak's dashboard shows your revenue and margin across all your jobs, so you can see how profitability shifts across seasons and adjust your pricing or workload accordingly.",
      },
      {
        question: "Can I include travel costs on pool technician call-out quotes?",
        answer:
          "Yes. Add travel as a line item with a fixed fee or a per-km rate. Include it in your service template so it appears automatically on every call-out quote.",
      },
    ],
  },
  {
    slug: "tilers",
    name: "Tilers",
    metaTitle: "Quoting Software for Tilers — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for tilers. Quote by m² with material, adhesive, and grout included, build templates for floor, wall, and bathroom jobs, and invoice with one click. Free to start.",
    heroHeadline: "Quoting software built for tilers",
    heroSubheading:
      "Quote by m² with materials and labour built in — and always know your margin before you start. QuoTrak gives tilers fast, accurate quotes and professional invoicing for every job.",
    icon: LayoutGrid,
    benefits: [
      "Quote by m² with material, adhesive, and grout included",
      "Build separate templates for floor, wall, and bathroom jobs",
      "Protect margins on tricky cuts and complex pattern work",
      "Convert quotes to invoices the moment the client signs off",
    ],
    painPoints: [
      "Tile prices vary between suppliers and ranges — a quote built on last month's pricing can erode your margin quickly",
      "Complex patterns and difficult cuts require a labour uplift that's easy to forget in a rushed quote",
      "Adhesive, grout, and waste are small costs that add up and can quietly reduce your profit on large tiling jobs",
    ],
    solutions: [
      "Build quotes with per-m² pricing including tiles, adhesive, grout, and a wastage allowance so nothing is left out",
      "Add a labour uplift line item for complex patterns, cuts, and wet areas so your price reflects the real difficulty",
      "Update tile unit costs before every quote so your margin reflects current supplier pricing",
    ],
    faqs: [
      {
        question: "Can I build separate tiling templates for floors, walls, and bathrooms?",
        answer:
          "Yes. Save templates for your most common job types — floor tiles, wall tiles, bathroom renovations — each with the appropriate materials, adhesive, grout, and labour rates pre-filled. Adjust quantities per job.",
      },
      {
        question: "How do I include tile wastage in my quotes?",
        answer:
          "Add a wastage line item as a percentage of the tile quantity, or increase your m² estimate to account for cuts and breakage. Either approach is visible in the quote and reflected in your margin calculation.",
      },
      {
        question: "Can I include adhesive and grout costs in a tiling quote?",
        answer:
          "Yes. Add adhesive and grout as separate line items with their unit costs and quantities. For templated jobs, include them in your saved template so they appear automatically on every quote.",
      },
      {
        question: "How do I handle price uplifts for complex pattern work or difficult cuts?",
        answer:
          "Add a labour uplift as a separate line item — either a fixed fee or an additional hourly rate for the extra complexity. This keeps your base rate clean while ensuring complex work is priced appropriately.",
      },
      {
        question: "Does QuoTrak work for a sole-trader tiler?",
        answer:
          "Yes. QuoTrak is built for solo tilers and small crews. Start on the free plan with no credit card required and upgrade when your business grows.",
      },
    ],
  },
  {
    slug: "fencers",
    name: "Fencers",
    metaTitle: "Quoting Software for Fencers — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for fencers. Quote by lineal metre with materials and post counts built in, build templates for Colorbond, timber, and pool fencing, and invoice with deposits. Free to start.",
    heroHeadline: "Quoting software built for fencers",
    heroSubheading:
      "Quote by lineal metre with materials and post counts built in — and protect your margin as material prices fluctuate. QuoTrak gives fencers fast, accurate quotes and professional invoicing.",
    icon: Shield,
    benefits: [
      "Quote by lineal metre with materials and post counts built in",
      "Build templates for Colorbond, timber, and pool fencing",
      "Track margin as material prices fluctuate",
      "Invoice with deposit and final payment splits for larger jobs",
    ],
    painPoints: [
      "Steel and timber prices change regularly — a quote built on last month's material rates can put you underwater",
      "Pool fencing and Colorbond installs have different cost structures; mixing them up in a rushed quote causes errors",
      "Larger fencing jobs with deposits and final payments are messy to track with manual or spreadsheet invoicing",
    ],
    solutions: [
      "Update material unit costs before every quote so your pricing reflects current steel, timber, and hardware prices",
      "Keep separate templates for Colorbond, timber, pool fencing, and retaining walls so the right costs load every time",
      "Issue deposit invoices at sign-off and final invoices on completion — all from the same accepted quote",
    ],
    faqs: [
      {
        question: "Can I quote fencing jobs by lineal metre?",
        answer:
          "Yes. Set up a per-lineal-metre line item for your fencing supply and installation, enter the run length, and QuoTrak calculates the total. Add post quantities, gate hardware, and footings as separate line items.",
      },
      {
        question: "Can I build separate templates for Colorbond, timber, and pool fencing?",
        answer:
          "Yes. Create a template for each fence type with the relevant materials, post counts, and labour rates pre-filled. Select the right template for each job and adjust quantities to suit the site.",
      },
      {
        question: "How do I handle fluctuating steel prices in fencing quotes?",
        answer:
          "Update the unit cost on your Colorbond or steel material line item before sending any quote. QuoTrak recalculates your total and margin instantly so your price always reflects current supplier costs.",
      },
      {
        question: "Does QuoTrak support deposit and final payment invoicing for fencing jobs?",
        answer:
          "Yes. Issue a deposit invoice when the job is signed off and a final invoice on completion — both linked to the original accepted quote for clean records and easy client communication.",
      },
      {
        question: "Does QuoTrak work for a sole-trader fencer or small fencing business?",
        answer:
          "Yes. QuoTrak is built for solo operators and small crews. Start free with no credit card required and upgrade when you need more advanced features.",
      },
    ],
  },
  {
    slug: "pest-control",
    name: "Pest Control",
    metaTitle: "Quoting Software for Pest Control Technicians — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software built for pest control businesses. Quote inspections, treatments, and follow-up visits separately, include chemical and travel costs, and keep full property treatment histories. Free to start.",
    heroHeadline: "Quoting software built for pest control businesses",
    heroSubheading:
      "Quote inspections, treatments, and follow-up visits accurately — and never forget a chemical cost or travel fee again. QuoTrak gives pest control operators professional quotes and complete property records.",
    icon: Bug,
    benefits: [
      "Quote inspections, treatments, and follow-up visits separately",
      "Include chemical costs and travel without forgetting them",
      "Build treatment templates for termites, rodents, and general pest",
      "Keep a full history of every property's treatment schedule",
    ],
    painPoints: [
      "Chemical costs and travel fees are easy to forget in a rushed quote, quietly reducing your margin on every job",
      "Clients expect detailed treatment reports and quotes — a vague price with no breakdown looks unprofessional",
      "Tracking which properties are due for follow-up treatments is difficult without organised client records",
    ],
    solutions: [
      "Build templates for termite inspections, general treatments, and follow-up visits with chemical and travel costs already included",
      "Send detailed, itemized quotes that break down treatment type, chemicals used, and visit costs so clients understand what they're paying for",
      "Keep a complete service and payment history for every property so follow-up scheduling and billing is always on top",
    ],
    faqs: [
      {
        question: "Can I build separate templates for termite, rodent, and general pest treatments?",
        answer:
          "Yes. Create a template for each treatment type with the relevant chemicals, labour, and travel costs pre-filled. Select the right template for each job and adjust quantities based on the property size.",
      },
      {
        question: "How do I include chemical costs in my pest control quotes?",
        answer:
          "Add chemical costs as line items with the product, quantity, and unit cost. Include them in your treatment templates so they appear automatically on every quote without having to add them manually each time.",
      },
      {
        question: "Can I track follow-up visit schedules for recurring clients?",
        answer:
          "QuoTrak keeps a complete quote and invoice history for every client. You can also set up recurring invoices for clients on regular treatment schedules so billing is handled automatically.",
      },
      {
        question: "Does QuoTrak support quoting for both residential and commercial pest control?",
        answer:
          "Yes. Manage homeowners and commercial property managers from the same client dashboard. Each property has its own quote and service history for easy reference.",
      },
      {
        question: "Can I include a travel fee on pest control call-out quotes?",
        answer:
          "Yes. Add a travel fee as a line item — either a fixed amount or a per-km rate. Include it in your template so it appears on every call-out quote automatically.",
      },
    ],
  },
  {
    slug: "renovation-remodeling",
    name: "Renovation & Remodeling",
    metaTitle: "Quoting Software for Renovation & Remodeling Contractors — QuoTrak",
    metaDescription:
      "QuoTrak is quoting and invoicing software for renovation and remodeling contractors. Build detailed proposals, track scope changes, manage milestone invoicing, and protect your margin on complex projects. Free to start.",
    heroHeadline: "Quoting software built for renovation & remodeling",
    heroSubheading:
      "Turn complex scope into clear, winning proposals every time. QuoTrak helps renovation contractors capture every line item, protect their margin, and keep clients aligned from first proposal to final invoice.",
    icon: Building2,
    benefits: [
      "Build detailed line-item proposals covering labour, materials, and subcontractors",
      "Track margin in real time as scope grows and costs come in",
      "Issue milestone invoices to match progress payments on longer jobs",
      "Keep clients aligned with professional, branded proposals",
    ],
    painPoints: [
      "Scope creep quietly eats your profit when additional work isn't tracked and billed as it happens",
      "Clients expect detailed proposals — a rough estimate loses the job to contractors who show their working",
      "Managing deposits, progress payments, and final billing manually across a long renovation is error-prone",
    ],
    solutions: [
      "Capture every cost — labour, materials, fixtures, subcontractors — in a clear, itemized quote before work starts",
      "Issue deposit invoices at sign-off, progress invoices at each milestone, and a final invoice on completion",
      "Send polished, branded proposals that justify your price and build client confidence before a single tool is picked up",
    ],
    faqs: [
      {
        question: "Can I build a detailed renovation proposal with multiple trades and materials?",
        answer:
          "Yes. QuoTrak's line-item quoting lets you capture every subcontractor, material category, and labour cost in one organized document. Group items by room or trade so the proposal is easy for clients to follow.",
      },
      {
        question: "How do I handle scope changes during a renovation project?",
        answer:
          "You can update an existing quote or create a variation quote for additional work. Every change is tracked with its own line items and margin so nothing slips through unbilled.",
      },
      {
        question: "Can I issue progress invoices tied to renovation milestones?",
        answer:
          "Yes. Issue a deposit invoice at the start, progress invoices at agreed milestones — demolition complete, framing done, fit-out finished — and a final invoice on completion, all linked to the original quote.",
      },
      {
        question: "Does QuoTrak support renovation projects with multiple subcontractors?",
        answer:
          "Yes. Add each subcontractor's cost as a line item with your markup. QuoTrak shows you the margin across all subcontractors and materials so you can see your total project profit before you commit.",
      },
      {
        question: "Can clients sign off on renovation quotes digitally?",
        answer:
          "QuoTrak generates professional PDF quotes that you can email directly to clients. Once they approve, you mark the quote as accepted and issue your first invoice — all from the same platform.",
      },
    ],
  },
];

export const industryPagesBySlug = new Map<string, IndustryPage>(
  industryPages.map((page) => [page.slug, page])
);
